/**
 * OfflineBanner — slim banner that appears across the top when the
 * device is offline OR when there are queued mutations awaiting replay.
 *
 * Reconnect behaviour: when the device comes back online, we fire the
 * client-side flushQueue() and post FLUSH_QUEUE to the SW. Both routes
 * are safe to invoke together — every queued row carries an
 * Idempotency-Key so a double-replay short-circuits to the cached
 * server response.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { useTranslation }              from 'react-i18next'
import { useOnlineStatus }             from '@/hooks/useOnlineStatus'
import { useQueuedMutations }          from '@/hooks/useQueuedMutations'
import { flushQueue, notifyServiceWorkerToFlush } from '@/lib/sync/flush'

export function OfflineBanner(): ReactNode {
  const { t }                        = useTranslation()
  const { online, lastTransitionAt } = useOnlineStatus()
  const { count, refresh }           = useQueuedMutations()
  const lastSeenOnlineRef            = useRef<boolean>(online)

  // Edge: offline → online. Fire both flush paths, then refresh count.
  useEffect(() => {
    const wasOffline = lastSeenOnlineRef.current === false
    lastSeenOnlineRef.current = online
    if (online && wasOffline) {
      void (async (): Promise<void> => {
        try { await flushQueue() } catch { /* surfaced via queue lastError */ }
        await notifyServiceWorkerToFlush()
        refresh()
      })()
    }
  }, [online, lastTransitionAt, refresh])

  if (online && count === 0) return null

  const bg    = online ? 'var(--brand-warning-subtle)' : 'var(--brand-danger-subtle)'
  const fg    = online ? 'var(--brand-warning)'        : 'var(--brand-danger)'
  const plural = count === 1 ? '' : 's'
  const text  =
    !online && count > 0 ? t('layout.offlineQueued', { count, plural }) :
    !online              ? t('layout.offlineNoQueue') :
                           t('layout.reconnecting', { count, plural })

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background:   bg,
        color:        fg,
        padding:      '0.4rem 1rem',
        fontSize:     '0.85rem',
        textAlign:    'center',
        borderBottom: `1px solid ${fg}`,
      }}
    >
      {text}
    </div>
  )
}
